/**
 * Karbon360° v2 — sidebar.
 *
 * Workflow IA (Collect → Report; Measure/Reduce arrive with their screens),
 * with data collection grouped by GHG Protocol scope. Every entry navigates for
 * real via react-router.
 *
 * Note: the previous sidebar read `window.location.pathname` directly, so the
 * active highlight went stale on client-side navigation. This one uses
 * useLocation, which re-renders on every route change.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from './Icons';
import { NAV, CBAM_GROUP_KEY } from './nav-model';
import { useActionInbox } from './useActionInbox';
import { useT } from '../../i18n';
import logo from '../../assets/karbon360-logo.svg';
import { useSession, clearSession } from './session';

/** Collect every route beneath a branch, so a parent can highlight for its children. */
function routesUnder(item, acc = []) {
  if (item.to) acc.push(item.to);
  (item.kids || []).forEach((k) => routesUnder(k, acc));
  return acc;
}

function firstLeaf(item) {
  return item.kids ? firstLeaf(item.kids[0]) : item.to;
}

export default function SidebarV2({ rail, setRail, onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  // Owner/admin pages are deliberately NOT in the sidebar — they live in the
  // account menu, so day-to-day navigation is the same for everyone.
  const { email, hasCbam, resolved } = useSession();
  const { t } = useT();

  // NAV is static; dynamic bits are applied at render (same as the inbox badge
  // below). The CBAM group stays hidden until the session has resolved AND the
  // plan qualifies — hide-until-known, so an entitled account never sees the
  // group blink in and an unentitled one never sees it at all.
  const groups = NAV.filter((g) => g.groupKey !== CBAM_GROUP_KEY || (resolved && hasCbam));

  // Outstanding-item count for the Action inbox row. Cached module-side, so this
  // is one request per session rather than one per navigation.
  const { counts: inboxCounts } = useActionInbox();
  const inboxCount = inboxCounts.open;

  const [open, setOpen] = useState({});

  // Auto-expand whichever branches contain the current route.
  useEffect(() => {
    const next = {};
    const visit = (items, trail) => {
      items.forEach((it) => {
        const t = [...trail, it.id];
        if (it.kids) {
          if (routesUnder(it).includes(path)) t.forEach((id) => { next[id] = true; });
          visit(it.kids, t);
        }
      });
    };
    NAV.forEach((g) => visit(g.items, []));
    setOpen((prev) => ({ ...prev, ...next }));
  }, [path]);

  const go = (to) => {
    if (!to) return;
    navigate(to);
    if (onNavigate) onNavigate();
  };

  const handleLogout = () => {
    try { localStorage.removeItem('token'); } catch { /* ignore */ }
    clearSession();
    if (onNavigate) onNavigate();
    navigate('/');
  };

  const Row = ({ item, depth = 0 }) => {
    const Ic = item.icon ? X[item.icon] : null;
    const label = t(item.labelKey);
    // NAV is static, so the inbox count is injected here rather than baked in.
    const badge = item.id === 'inbox'
      ? (inboxCount > 0 ? String(inboxCount) : null)
      : item.badge;
    const hasKids = !!item.kids;
    const expanded = !!open[item.id];
    const isExact = item.to === path;
    // A collapsed parent shows the highlight on behalf of its active child.
    const isOn = isExact || (hasKids && !expanded && routesUnder(item).includes(path));

    const cls = [
      'k2-nav',
      depth === 1 ? 'k2-d1' : depth === 2 ? 'k2-d2' : '',
      isOn ? 'k2-on' : '',
      expanded ? 'k2-exp' : '',
    ].filter(Boolean).join(' ');

    return (
      <>
        <button
          type="button"
          className={cls}
          // Long scope labels ellipsis inside the 260px rail — expose the full
          // text on hover, and give the button an explicit accessible name.
          title={label}
          aria-label={label}
          aria-expanded={hasKids ? expanded : undefined}
          aria-current={isExact ? 'page' : undefined}
          onClick={() => {
            if (hasKids) {
              setOpen((o) => ({ ...o, [item.id]: !o[item.id] }));
              if (!expanded) go(firstLeaf(item));
            } else {
              go(item.to);
            }
          }}
        >
          {Ic && <span className="k2-nav-i"><Ic s={17} /></span>}
          <span className="k2-nav-t">{label}</span>
          {badge && <span className="k2-nav-b">{badge}</span>}
          {hasKids && <span className="k2-nav-cv"><X.Chev s={12} /></span>}
          {rail && depth === 0 && <span className="k2-tip">{label}</span>}
        </button>

        {hasKids && expanded && (
          <div className="k2-sub">
            {item.kids.map((k) => <Row key={k.id} item={k} depth={depth + 1} />)}
          </div>
        )}
      </>
    );
  };

  return (
    <aside className="k2-side">
      <button
        type="button"
        className="k2-rail-tog"
        onClick={() => setRail(!rail)}
        aria-label={rail ? t('chrome.expandSidebar') : t('chrome.collapseSidebar')}
      >
        {rail ? <X.Chev s={12} /> : <X.ChevL s={12} />}
      </button>

      <div className="k2-side-top">
        <div className="k2-brandrow">
          {/* Placeholder mark — swap for the real logo vectors when supplied. */}
          {/* The real lockup — white wordmark + mint mark, drawn for a dark ground,
              which is exactly what the slate sidebar is. Collapsed to the rail
              there is no room for the wordmark, so the icon mark stands in. */}
          <img src={logo} alt="Karbon360°" className="k2-logo" />
          <span className="k2-brandmark k2-railonly"><X.Karbon s={26} /></span>
        </div>
        <div className="k2-org" title={email || t('chrome.workspace')}>
          <span className="k2-org-mark">K3</span>
          <span className="k2-org-txt">
            <span className="k2-org-name">{email ? email.split('@')[0] : t('chrome.workspace')}</span>
            <span className="k2-org-meta">Karbon360°</span>
          </span>
        </div>
      </div>

      <div className="k2-side-scroll">
        {groups.map((g, i) => (
          <div className="k2-grp" key={g.groupKey || `g${i}`}>
            {g.groupKey && <div className="k2-grp-h"><span>{t(g.groupKey)}</span></div>}
            {g.items.map((it) => <Row key={it.id} item={it} />)}
          </div>
        ))}

      </div>

      <div className="k2-side-foot">
        <button type="button" className="k2-nav" onClick={handleLogout}>
          <span className="k2-nav-i"><X.Log s={17} /></span>
          <span className="k2-nav-t">{t('chrome.signOut')}</span>
          {rail && <span className="k2-tip">{t('chrome.signOut')}</span>}
        </button>
      </div>
    </aside>
  );
}
